/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
import React, { createContext, useState, useEffect, useContext, Suspense, lazy } from 'react';
import { extractErrorMessage, logError } from './utils/errorHandler';
import { apiGet, apiPost, applyDefaultAuthHeader } from './services/apiClient';
import BranchSelector from './components/branches/BranchSelector';
import useActiveBranch from './hooks/useActiveBranch';
import { clearAllAuthStorage, clearAuthStorage, clearSessionExpired, getAuthContext, getAuthToken, isSessionExpired, markSessionExpired, persistAuthToken, setAuthContext } from './services/authStorage';
import { normalizeLoginPayload, validateLoginPayload } from './utils/loginValidation';
import { buildBackendUrl } from './utils/backendUrl';
import { installExitBackupBrowserGuards, runMandatoryLogoutBackup, sendBackupHeartbeat } from './services/exitBackupGuard';

// -------------------------------------------------------------------
// Eager imports: small, always-needed components (initial paint path)
// -------------------------------------------------------------------
import SummaryDashboard from './components/SummaryDashboard';
import CustomerModal from './components/CustomerModal';
import SupplierModal from './components/SupplierModal';
import AccountModal from './components/AccountModal';
import NotificationBell from './components/notifications/NotificationBell';
import { CacheProvider, PerformanceMonitor } from './components/PerformanceOptimization';

// -------------------------------------------------------------------
// Lazy imports: heavy screens loaded on-demand (code splitting).
// This cuts ~2MB from the initial JS bundle. Users only pay the cost
// for the module they actually open, and the download is cached
// aggressively (immutable hashed chunks).
// -------------------------------------------------------------------
const ChartOfAccountsCenter = lazy(() => import('./components/ChartOfAccountsCenter'));
const JournalEntries = lazy(() => import('./components/JournalEntries'));
const CustomersManager = lazy(() => import('./components/CustomersManager'));
const SuppliersManager = lazy(() => import('./components/SuppliersManager'));
const BankAccountsManager = lazy(() => import('./components/banking/BankAccountsManager'));
const BackupManager = lazy(() => import('./components/BackupManager'));
const ReceiptsManager = lazy(() => import('./components/ReceiptsManager'));
const PaymentsManager = lazy(() => import('./components/PaymentsManager'));
const InterAccountTransfersManager = lazy(() => import('./components/InterAccountTransfersManager'));
const BankReconciliationsManager = lazy(() => import('./components/BankReconciliationsManager'));
const SalesQuotesManager = lazy(() => import('./components/SalesQuotesManager'));
const SalesOrdersManager = lazy(() => import('./components/SalesOrdersManager'));
const CreditNotesManager = lazy(() => import('./components/CreditNotesManager'));
const DeliveryNotesManager = lazy(() => import('./components/DeliveryNotesManager'));
const CustomerInvoiceCenter = lazy(() => import('./components/invoices/CustomerInvoiceCenter'));
const PurchaseInvoicesManager = lazy(() => import('./components/invoices/PurchaseInvoicesManager'));
const ExpenseInvoicesManager = lazy(() => import('./components/invoices/ExpenseInvoicesManager'));
const RecurringInvoicesManager = lazy(() => import('./components/invoices/RecurringInvoicesManager'));
const PurchaseQuotesManager = lazy(() => import('./components/PurchaseQuotesManager'));
const PurchaseOrdersManager = lazy(() => import('./components/PurchaseOrdersManager'));
const DebitNotesManager = lazy(() => import('./components/DebitNotesManager'));
const GoodsReceiptsManager = lazy(() => import('./components/GoodsReceiptsManager'));
const UserManagementManager = lazy(() => import('./components/UserManagementManager'));
const SystemSettingsManager = lazy(() => import('./components/SystemSettingsManager'));
const InventoryManager = lazy(() => import('./components/InventoryManager'));
const HRManager = lazy(() => import('./components/HRManager'));
const AssetsManager = lazy(() => import('./components/AssetsManager'));
const ManufacturingManager = lazy(() => import('./components/manufacturing/ManufacturingManager'));
const BOMManager = lazy(() => import('./components/manufacturing/BOMManager'));
const WorkOrderManager = lazy(() => import('./components/manufacturing/WorkOrderManager'));
const InvestmentsManager = lazy(() => import('./components/investments/InvestmentsManager'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const ReportsManager = lazy(() => import('./components/ReportsManager'));
const BranchTrialBalanceReports = lazy(() => import('./components/branches/BranchTrialBalanceReports'));
const InterBranchTransferManager = lazy(() => import('./components/branches/InterBranchTransferManager'));
const CompanySettings = lazy(() => import('./components/CompanySettings'));
const AdvancedDashboard = lazy(() => import('./components/AdvancedDashboard'));
const AdvancedSettings = lazy(() => import('./components/AdvancedSettings'));
const DiagnosticsCenter = lazy(() => import('./components/DiagnosticsCenter'));
const EInvoiceControlCenter = lazy(() => import('./components/einvoice/EInvoiceControlCenter'));
const FinancialIntegrityCenter = lazy(() => import('./components/FinancialIntegrityCenter'));
const CodeRuntimeInspector = lazy(() => import('./components/CodeRuntimeInspector'));
const LocalAutoRepairAgent = lazy(() => import('./components/LocalAutoRepairAgent'));
const FullSystemAuditor = lazy(() => import('./components/FullSystemAuditor'));
const ManagerMigration = lazy(() => import('./components/ManagerMigration'));
const AutoRepairManager = lazy(() => import('./components/AutoRepairManager'));
const AuditLogsManager = lazy(() => import('./components/settings/AuditLogsManager'));
const PeriodLockingManager = lazy(() => import('./components/settings/PeriodLockingManager'));
const SmartDashboard = lazy(() => import('./components/SmartDashboard'));
const AdvancedAnalytics = lazy(() => import('./components/AdvancedAnalytics'));
const CompanySubscriptionStatus = lazy(() => import('./components/CompanySubscriptionStatus'));
const PDCManager = lazy(() => import('./components/PDCManager'));
const ProjectsManager = lazy(() => import('./components/ProjectsManager'));
const ExpenseClaimsManager = lazy(() => import('./components/ExpenseClaimsManager'));
const RecurringJournalsManager = lazy(() => import('./components/RecurringJournalsManager'));
const EnhancedDashboard = lazy(() => import('./components/EnhancedDashboard'));
const SavOneCommandCenter = lazy(() => import('./components/savOne/SavOneCommandCenter'));
const TaxComplianceCenter = lazy(() => import('./components/savOne/TaxComplianceCenter'));
const SmartBankReconciliationCenter = lazy(() => import('./components/savOne/SmartBankReconciliationCenter'));
const SavAIAdvisor = lazy(() => import('./components/savOne/SavAIAdvisor'));
const SmartInventoryCenter = lazy(() => import('./components/savOne/SmartInventoryCenter'));
const TaskManagement = lazy(() => import('./components/TaskManagement'));
const SuperAdminApp = lazy(() => import('./admin/SuperAdminApp'));
const EnhancedSalesManager = lazy(() => import('./components/EnhancedSalesManager'));
const PayrollVouchers = lazy(() => import('./components/PayrollVouchers'));
const EmployeePortal = lazy(() => import('./components/EmployeePortal'));
const BatchPrintCenter = lazy(() => import('./components/BatchPrintCenter'));
const GlobalDocumentSearch = lazy(() => import('./components/GlobalDocumentSearch'));
const AutoSettlementCenter = lazy(() => import('./components/AutoSettlementCenter'));
const SmartInvoiceScannerManager = lazy(() => import('./components/smart-invoice-scanner/SmartInvoiceScannerManager'));
const GoldKaratSettings = lazy(() => import('./components/GoldKaratSettings'));

// Suspense fallback shown briefly while a lazy chunk downloads.
const LazyFallback = () => (
  <div style={{ padding: '48px 24px', textAlign: 'center', color: '#64748b' }} data-testid="lazy-loading-fallback">
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
      <span
        style={{
          width: '18px',
          height: '18px',
          border: '2px solid #cbd5e1',
          borderTopColor: '#2563eb',
          borderRadius: '50%',
          display: 'inline-block',
          animation: 'sav-erp-spin 0.9s linear infinite',
        }}
      />
      <span style={{ fontSize: '14px' }}>جاري تحميل الوحدة...</span>
    </div>
    <style>{`@keyframes sav-erp-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const normalizeUserShape = (rawUser) => {
  if (!rawUser) return null;
  const candidate = { ...rawUser };
  if (candidate?.role === 'super_admin' && candidate?.acting_as_company) {
    candidate.company_id = candidate.company_id || candidate.acting_company_id || candidate.company_id;
    candidate.company_code = candidate.company_code || candidate.acting_company_code || candidate.company_code;
  }
  if (candidate?.role !== 'super_admin' && !candidate?.company_id && candidate?.company_code) {
    candidate.company_id = candidate.company_code;
  }
  return candidate;
};

const authContextForUser = (candidateUser = null) => {
  if (candidateUser?.role === 'super_admin') {
    return candidateUser?.acting_as_company || candidateUser?.session_context === 'erp' ? 'erp' : 'superadmin';
  }
  return 'erp';
};

// Language Context for ERP-wide translations
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Return default values if not in context (for standalone components)
    return {
      language: 'ar',
      t: (ar, en) => ar,
      toggleLanguage: () => {}
    };
  }
  return context;
};

// Session activity tracker - keep session alive
let lastActivity = Date.now();
const SESSION_TIMEOUT_WARNING = 23 * 60 * 60 * 1000; // 23 hours
const ERP_AUTH_READY_KEY = 'sav_erp_auth_ready';

const VIEW_ROUTE_ALIASES = {
  sales: 'sales-invoices',
  purchases: 'purchase-invoices',
  reconciliations: 'reconciliation',
  'production-orders': 'work-orders',
  payslips: 'payroll-vouchers',
  'fixed-assets': 'assets',
  depreciation: 'assets',
  'capital-accounts': 'chart-of-accounts',
  'special-accounts': 'chart-of-accounts',
  folders: 'backup',
  'invoice-scanner': 'smart-invoice-scanner',
  'smart-invoice-scanner': 'smart-invoice-scanner',
};

const resolveViewRoute = (viewId) => {
  const candidate = typeof viewId === 'object' && viewId !== null
    ? (viewId.view || viewId.id || viewId.route || viewId.target || viewId.currentView || '')
    : viewId;
  const raw = String(candidate || '').trim().replace(/^\/+/, '');
  if (!raw) return 'dashboard';
  return VIEW_ROUTE_ALIASES[raw] || raw;
};

const resolveInitialViewFromLocation = () => {
  if (typeof window === 'undefined') return resolveViewRoute('dashboard');
  try {
    const params = new URLSearchParams(window.location.search || '');
    const queryView = params.get('view') || params.get('page') || params.get('route');
    if (queryView) return resolveViewRoute(queryView);
    const pathView = String(window.location.pathname || '').replace(/^\/+/, '').trim();
    if (pathView && pathView !== 'index.html') return resolveViewRoute(pathView);
  } catch (_error) {
    // keep dashboard as a safe fallback
  }
  return resolveViewRoute('dashboard');
};

const syncViewToAddressBar = (viewId) => {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  try {
    const resolved = resolveViewRoute(viewId);
    const url = new URL(window.location.href);
    url.pathname = '/';
    if (resolved && resolved !== 'dashboard') url.searchParams.set('view', resolved);
    else url.searchParams.delete('view');
    window.history.replaceState({ view: resolved }, '', `${url.pathname}${url.search}${url.hash}`);
  } catch (_error) {
    // navigation state is best effort only
  }
};

const updateLastActivity = () => {
  lastActivity = Date.now();
};

const setErpAuthReady = (isReady) => {
  if (typeof window === 'undefined') return;
  try {
    if (isReady) {
      window.sessionStorage.setItem(ERP_AUTH_READY_KEY, '1');
    } else {
      window.sessionStorage.removeItem(ERP_AUTH_READY_KEY);
    }
  } catch (_err) {
    // ignore storage errors in restricted environments
  }
};

const BRAND_PROFILE = {
  productName: 'SAV_ERP',
  productNameAr: 'نظام SAV_ERP',
  taglineAr: 'منصة موحدة لإدارة الحسابات والمخزون والمبيعات والمشتريات والتقارير',
  taglineEn: 'Unified accounting, inventory, sales, purchasing, and reporting platform',
  editionAr: 'Business Suite',
  editionEn: 'Business Suite',
  supportAr: 'إدارة مالية وتشغيلية متكاملة',
  supportEn: 'Integrated financial and operational management',
};


const CLIENT_BUILD_LABEL = '2026-04-26-customer-e2e-r24-2026-04-29-customer-identity-r55';

const FIRST_RUN_ACTIONS = [
  { id: 'onboarding-company-settings', target: 'company-settings', titleAr: 'إعداد بيانات الشركة', titleEn: 'Set up company profile', noteAr: 'أدخل بيانات الشركة والعملة والضريبة لتظهر بشكل صحيح في الفواتير والتقارير.', noteEn: 'Enter company, currency, and tax defaults for accurate invoices and reports.' },
  { id: 'onboarding-backup', target: 'backup', titleAr: 'إعداد النسخ الاحتياطي', titleEn: 'Configure backups', noteAr: 'فعّل النسخ الاحتياطي الدوري لحماية بيانات الشركة واستمرارية العمل.', noteEn: 'Enable scheduled backups to protect company data and business continuity.' },
  { id: 'onboarding-diagnostics', target: 'diagnostics', titleAr: 'مراجعة صحة النظام', titleEn: 'Review system health', noteAr: 'تابع حالة الاتصال والخدمات والتنبيهات المهمة من مركز التشخيص.', noteEn: 'Review connectivity, services, and important alerts from diagnostics.' },
  { id: 'onboarding-subscription-status', target: 'subscription-status', titleAr: 'حالة التفعيل', titleEn: 'Activation status', noteAr: 'راجع حالة الترخيص والتفعيل لضمان استمرار استخدام النظام.', noteEn: 'Review license and activation status to keep the system available.' },
];

const makeInitials = (value = '') => {
  const text = String(value || '').trim();
  if (!text) return 'ERP';
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  return text.slice(0, 3).toUpperCase();
};

const getCompanyDisplayName = (settings = {}, currentUser = {}, language = 'ar') => {
  const arName = settings.company_name_ar || currentUser?.company_name_ar || '';
  const enName = settings.company_name || currentUser?.company_name || currentUser?.company_code || '';
  return language === 'ar' ? (arName || enName || 'منشأتك') : (enName || arName || 'Your company');
};

const getCompanyDisplaySubtitle = (settings = {}, currentUser = {}, language = 'ar') => {
  const arSubtitle = settings.company_address_ar || settings.company_address || settings.company_email || settings.company_phone || currentUser?.company_code || '';
  const enSubtitle = settings.company_address || settings.company_address_ar || settings.company_email || settings.company_phone || currentUser?.company_code || '';
  return language === 'ar' ? (arSubtitle || 'إدارة مالية وتشغيلية') : (enSubtitle || 'Business operations');
};

const getCompanyLogo = (settings = {}) => settings.company_logo || settings.logo || '';

const BrandMark = ({ compact = false, prominent = false, className = '', logoSrc = '', label = '', systemIdentity = false }) => {
  const outerClass = prominent
    ? 'h-20 w-40 rounded-3xl md:h-24 md:w-48'
    : compact
      ? 'h-14 w-24 rounded-2xl'
      : 'h-24 w-52 rounded-3xl md:h-28 md:w-60';
  const resolvedLogo = logoSrc || (systemIdentity ? '/system-identity/sav-official-logo.png' : '');
  return (
    <div className={`${outerClass} ${className} relative overflow-hidden bg-white text-slate-900 shadow-lg shadow-blue-950/20 ring-1 ring-slate-200`}>
      {resolvedLogo ? (
        <img src={resolvedLogo} alt={label || 'Company logo'} className="h-full w-full object-contain p-0.5" />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 px-2 text-center text-sm font-black tracking-wide text-blue-900">
          {makeInitials(label)}
        </div>
      )}
    </div>
  );
};

const DeliveryBadge = ({ children, tone = 'dark' }) => {
  const tones = {
    dark: 'bg-slate-950 text-white shadow-lg shadow-slate-900/10',
    light: 'bg-white/90 text-slate-800 ring-1 ring-slate-200',
    blue: 'bg-blue-50 text-blue-800 ring-1 ring-blue-200',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${tones[tone] || tones.dark}`}>
      {children}
    </span>
  );
};

// Track user activity
if (typeof window !== 'undefined') {
  ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
    window.addEventListener(event, updateLastActivity, { passive: true });
  });
}

const FullERPApp = () => {
  const isEmployeePortalRoute = typeof window !== 'undefined' && (window.location.pathname.includes('employee-portal') || new URLSearchParams(window.location.search || '').get('view') === 'employee-portal');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState(resolveInitialViewFromLocation());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showModal, setShowModal] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    company_name_ar: '',
    logo: '',
    currency: 'AED',
    currency_symbol: 'د.إ'
  });

  // Fetch company settings
  const fetchCompanySettings = async (currentUser = null) => {
    try {
      const token = getAuthToken('erp');
      if (!token) {
        applyDefaultAuthHeader('');
        return;
      }

      const resolvedUser = currentUser || user;
      if (resolvedUser?.role === 'super_admin' && !resolvedUser?.acting_as_company) {
        return;
      }
      
      const response = await apiGet('/company-settings', { authContext: 'erp' });
      
      if (response) {
        setCompanySettings({
          ...response,
          company_name: response.company_name || '',
          company_name_ar: response.company_name_ar || '',
          company_logo: response.company_logo || response.logo || '',
          logo: response.company_logo || response.logo || '',
          currency: response.default_currency || response.currency || 'AED',
          currency_symbol: response.currency_symbol || 'د.إ'
        });
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  // Check authentication on app load
  useEffect(() => {
    setAuthContext('erp');
    checkAuthStatus();
  }, []);

  useEffect(() => {
    const onNavigate = (event) => {
      const requestedView = resolveViewRoute(event?.detail);
      if (!requestedView) return;
      setCurrentView(requestedView);
    };

    const onPopState = () => {
      setCurrentView(resolveInitialViewFromLocation());
    };

    window.addEventListener('navigateTo', onNavigate);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('navigateTo', onNavigate);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  useEffect(() => {
    syncViewToAddressBar(currentView);
  }, [currentView]);


  // V10.101 Exit Backup Guard: keep the server aware of the active company and
  // trigger a mandatory local backup if the browser/tab is closed unexpectedly.
  useEffect(() => {
    if (!user || authContextForUser(user) !== 'erp') return undefined;
    sendBackupHeartbeat({ event: 'session_open' });
    const uninstall = installExitBackupBrowserGuards();
    const heartbeatId = window.setInterval(() => {
      sendBackupHeartbeat({ event: 'heartbeat' });
    }, 60000);
    return () => {
      window.clearInterval(heartbeatId);
      if (typeof uninstall === 'function') uninstall();
    };
  }, [user]);

  // React quickly to invalidated sessions so the UI does not stay mounted in a broken state.
  useEffect(() => {
    const handleSessionExpiry = () => {
      const resolvedContext = authContextForUser(user);
      const contextsToCheck = user ? [resolvedContext] : ['superadmin', 'erp'];
      let handled = false;
      for (const context of contextsToCheck) {
        if (!isSessionExpired(context)) continue;
        clearAuthStorage(context);
        clearSessionExpired(context);
        handled = true;
      }
      if (handled) {
        applyDefaultAuthHeader('');
        setErpAuthReady(false);
        setUser(null);
        setLoading(false);
        setAuthContext('erp');
      }
    };

    handleSessionExpiry();
    const intervalId = window.setInterval(handleSessionExpiry, 10000);
    const watchedKeys = new Set([
      'sessionExpired:erp',
      'sessionExpired:superadmin',
      'sav_erp_erp_auth_token',
      'sav_erp_superadmin_auth_token',
      'authToken',
      'auth_token',
      'token',
    ]);
    const onStorage = (event) => {
      if (!event || event.key === null || watchedKeys.has(event.key)) {
        handleSessionExpiry();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('storage', onStorage);
    };
  }, [user]);

  useEffect(() => {
    const autoCheckpointsEnabled = String(process.env.REACT_APP_ENABLE_AUTO_CHECKPOINTS || '').toLowerCase() === 'true'
      || (typeof window !== 'undefined' && window.localStorage.getItem('sav_erp_enable_auto_checkpoints') === 'true');
    if (!autoCheckpointsEnabled) return undefined;
    const runtimeContext = authContextForUser(user);
    const companyId = user?.company_id || user?.companyId || '';
    if (!user || runtimeContext !== 'erp' || !companyId) return undefined;
    const token = getAuthToken('erp');
    if (!token) return undefined;
    if (typeof window !== 'undefined' && window.sessionStorage.getItem(ERP_AUTH_READY_KEY) !== '1') return undefined;

    const sendCheckpoint = (trigger) => {
      try {
        fetch(buildBackendUrl('/api/backups/auto-checkpoint'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ trigger }),
          keepalive: true,
        }).catch(() => {});
      } catch (_) {
        // best effort only
      }
    };

    const sessionOpenTimer = window.setTimeout(() => sendCheckpoint('session_open'), 1500);

    const handlePageHide = () => sendCheckpoint('pagehide');
    const handleBeforeUnload = () => sendCheckpoint('beforeunload');
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendCheckpoint('browser_close');
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearTimeout(sessionOpenTimer);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  const checkAuthStatus = async () => {
    const storedContext = getAuthContext();
    const contexts = [];
    const pushContext = (context) => {
      if (!context || contexts.includes(context)) return;
      const token = getAuthToken(context);
      if (!token) return;
      if (isSessionExpired(context)) return;
      contexts.push(context);
    };

    pushContext(storedContext);
    pushContext(storedContext === 'superadmin' ? 'erp' : 'superadmin');

    try {
      if (contexts.length === 0) {
        applyDefaultAuthHeader('');
        setErpAuthReady(false);
        setUser(null);
        setLoading(false);
        return;
      }

      for (const context of contexts) {
        const token = getAuthToken(context);
        try {
          setAuthContext(context);
          if (context === 'erp' && token) {
            applyDefaultAuthHeader(token);
          } else {
            applyDefaultAuthHeader('');
          }
          clearSessionExpired(context);

          const response = await apiGet('/auth/me', { skipAuthRedirect: true, authContext: context });
          if (!response) {
            clearAuthStorage(context);
            markSessionExpired(context);
            continue;
          }

          const userPayload = normalizeUserShape(response);
          const isSuperAdmin = userPayload?.role === 'super_admin';
          if (context === 'superadmin' && !isSuperAdmin) {
            clearAuthStorage('superadmin');
            markSessionExpired('superadmin');
            continue;
          }

          const isActingAsCompany = !!userPayload?.acting_as_company;
          if (context === 'erp' && isSuperAdmin && !isActingAsCompany && token) {
            clearAuthStorage('erp');
            clearSessionExpired('erp');
            persistAuthToken(token, 'superadmin');
            setAuthContext('superadmin');
          }

          const resolvedContext = authContextForUser(userPayload);
          setErpAuthReady(resolvedContext === 'erp');
          setAuthContext(resolvedContext);
          if (resolvedContext === 'erp') {
            applyDefaultAuthHeader(getAuthToken('erp'));
            fetchCompanySettings(userPayload);
          } else {
            applyDefaultAuthHeader('');
          }
          setUser(userPayload);
          return;
        } catch (error) {
          const status = error?.response?.status;
          const isAuthFailure = status === 401 || status === 403;
          if (isAuthFailure) {
            clearAuthStorage(context);
            markSessionExpired(context);
            continue;
          }
          console.warn('Auth check failed:', error);
          break;
        }
      }

      applyDefaultAuthHeader('');
      setErpAuthReady(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    const requestedContext = credentials?.company_code ? 'erp' : 'superadmin';
    const oppositeContext = requestedContext === 'erp' ? 'superadmin' : 'erp';
    try {
      setAuthContext(requestedContext);
      clearSessionExpired(requestedContext);
      clearSessionExpired(oppositeContext);
      clearAuthStorage(requestedContext);
      clearAuthStorage(oppositeContext);
      setErpAuthReady(false);
      applyDefaultAuthHeader('');

      const response = await apiPost('/auth/login', credentials, {
        skipAuthRedirect: true,
        skipAuthToken: true,
        authContext: requestedContext,
      });

      if (!response?.access_token) {
        return { success: false, error: 'Invalid response' };
      }

      const runtimeContext = authContextForUser(normalizeUserShape(response?.user || null));
      persistAuthToken(response.access_token, runtimeContext);
      setAuthContext(runtimeContext);
      clearSessionExpired(runtimeContext);
      if (runtimeContext === 'erp') {
        applyDefaultAuthHeader(response.access_token);
      } else {
        applyDefaultAuthHeader('');
      }

      let nextUser = null;
      let lastMeError = null;
      for (let attempt = 0; attempt < 5; attempt += 1) {
        try {
          const me = await apiGet('/auth/me', { skipAuthRedirect: true, authContext: runtimeContext });
          if (me) {
            nextUser = normalizeUserShape(me);
            break;
          }
        } catch (meError) {
          lastMeError = meError;
          const status = meError?.response?.status;
          if (status === 401 || status === 403) {
            await new Promise((resolve) => window.setTimeout(resolve, 250 * (attempt + 1)));
            continue;
          }
          throw meError;
        }
      }

      if (!nextUser || authContextForUser(nextUser) !== runtimeContext) {
        clearAuthStorage(runtimeContext);
        if (runtimeContext === 'erp') applyDefaultAuthHeader('');
        markSessionExpired(runtimeContext);
        return {
          success: false,
          error: extractErrorMessage(lastMeError, 'تعذر تثبيت الجلسة بعد تسجيل الدخول. يرجى المحاولة مرة أخرى.'),
        };
      }

      setErpAuthReady(authContextForUser(nextUser) === 'erp');
      setUser(nextUser);
      if (authContextForUser(nextUser) === 'erp') {
        fetchCompanySettings(nextUser);
      }
      return { success: true };
    } catch (error) {
      clearAllAuthStorage();
      setErpAuthReady(false);
      applyDefaultAuthHeader('');
      logError('FullERP Login', error);
      return {
        success: false,
        error: extractErrorMessage(error, 'خطأ في تسجيل الدخول')
      };
    }
  };

  const logout = async () => {
    const runtimeContext = authContextForUser(user);
    try {
      if (runtimeContext === 'erp') {
        await runMandatoryLogoutBackup();
      }
    } catch (backupError) {
      logError(backupError, 'Mandatory logout backup');
      // Do not trap the employee in the UI forever, but the backend /auth/logout
      // also runs the same guard as a second safety net.
    }
    try {
      await apiPost('/auth/logout', undefined, { authContext: runtimeContext, skipAuthRedirect: true });
    } catch (_error) {
      // Ignore transport failures during logout and clear local state anyway.
    } finally {
      clearAllAuthStorage();
      setErpAuthReady(false);
      applyDefaultAuthHeader('');
      setAuthContext('erp');
      setUser(null);
      setCurrentView('dashboard');
    }
  };

  if (isEmployeePortalRoute) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <EmployeePortal />
      </Suspense>
    );
  }

  // If user is super admin outside company-context, show Super Admin Panel
  if (user?.role === 'super_admin' && !user?.acting_as_company) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <SuperAdminApp user={user} logout={logout} />
      </Suspense>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">جاري تحميل النظام...</p>
        </div>
      </div>
    );
  }

  // Login form
  if (!user) {
    return <LoginForm onLogin={login} />;
  }

  // Main ERP App
  return (
    <CacheProvider>
      <AuthContext.Provider value={{
        user,
        login,
        logout,
        notifications,
        setNotifications
      }}>
        <div className="min-h-screen bg-gray-50 flex">
          <PerformanceMonitor>
            <ERPInterface 
              user={user}
              currentView={currentView}
              setCurrentView={setCurrentView}
              sidebarCollapsed={sidebarCollapsed}
              setSidebarCollapsed={setSidebarCollapsed}
              showModal={showModal}
              setShowModal={setShowModal}
              logout={logout}
              companySettings={companySettings}
            />
          </PerformanceMonitor>
        </div>
      </AuthContext.Provider>
    </CacheProvider>
  );
};

// Login Form Component
const LoginForm = ({ onLogin }) => {
  const [loginType, setLoginType] = useState('company');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    company_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const validationMessage = validateLoginPayload({ loginType, formData, language: 'ar' });
    if (validationMessage) {
      setError(validationMessage);
      setLoading(false);
      return;
    }

    const loginData = normalizeLoginPayload({ loginType, formData });

    const result = await onLogin(loginData);

    if (!result.success) {
      setError(extractErrorMessage(result.error, 'خطأ في تسجيل الدخول'));
    }

    setLoading(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const deliveryFeatures = [
    'إدارة الحسابات اليومية والفواتير والتحصيلات من شاشة واحدة',
    'متابعة المخزون والمشتريات والمبيعات مع أثر محاسبي منضبط',
    'تقارير مالية وتشغيلية تساعد الإدارة على اتخاذ قرارات أسرع',
  ];

  return (
    <div className="sav-brand-shell min-h-screen relative overflow-hidden px-4 py-8 md:px-8 lg:px-10">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-6xl items-center">
        <div className="grid w-full gap-8 lg:grid-cols-[1.08fr_0.92fr] lg:gap-10">
          <section className="hidden lg:flex sav-glass-card flex-col justify-between rounded-[32px] border border-white/70 p-8 xl:p-10">
            <div>
              <DeliveryBadge>{loginType === 'super_admin' ? 'Professional ERP Platform' : 'Company ERP Platform'}</DeliveryBadge>
              <div className="mt-6 max-w-xl">
                <h1 className="text-4xl font-black tracking-tight text-slate-950 xl:text-5xl">{loginType === 'super_admin' ? BRAND_PROFILE.productName : 'منصة إدارة الأعمال'}</h1>
                <p className="mt-3 text-base leading-8 text-slate-600">{loginType === 'super_admin' ? BRAND_PROFILE.taglineAr : 'إدارة الحسابات والفواتير والمخزون والتقارير بهوية مؤسسية مرتبة وواضحة.'}</p>
              </div>
              <div className="sav-brand-divider mt-8 h-px w-full rounded-full" />
              <div className="mt-8 grid gap-4 xl:grid-cols-2">
                {deliveryFeatures.map((item) => (
                  <div key={item} className="flex min-h-[110px] items-start gap-3 rounded-3xl border border-slate-200 bg-white/85 px-5 py-5 shadow-sm">
                    <span className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-950 text-xs font-bold text-white">✓</span>
                    <p className="text-sm leading-7 text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[24px] bg-slate-950 px-5 py-5 text-white shadow-xl shadow-slate-900/10">
                <p className="text-sm font-semibold">إدارة مالية وتشغيلية متكاملة</p>
                <p className="mt-2 text-sm leading-7 text-slate-200">
                  تابع الحسابات والفواتير والمخزون والمشتريات والمبيعات والتقارير من لوحة واحدة واضحة.
                </p>
              </div>
              <div className="rounded-[24px] border border-slate-200 bg-white/85 px-5 py-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Business Suite</p>
                <p className="mt-3 text-base font-semibold text-slate-900">واجهة مرتبة وسريعة الوصول</p>
                <p className="mt-2 text-sm leading-7 text-slate-600">تجربة استخدام أكثر هدوءًا وتركيزًا مع الحفاظ على هوية النظام الرسمية.</p>
              </div>
            </div>
          </section>

          <section className="sav-glass-card w-full rounded-[32px] border border-white/70 p-6 shadow-2xl shadow-slate-900/10 md:p-8 xl:p-10">
            <div className="flex flex-wrap items-start justify-between gap-5">
              <div className="flex items-center gap-4">
                <BrandMark prominent className="shrink-0" systemIdentity label={BRAND_PROFILE.productName || 'SAV_ERP'} />
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-700">{loginType === 'super_admin' ? 'Smart Account Vision' : 'Company ERP Access'}</p>
                  <h2 className="mt-1 text-3xl font-black tracking-tight text-slate-950">تسجيل الدخول</h2>
                  <p className="mt-2 text-sm leading-7 text-slate-500">{BRAND_PROFILE.supportAr}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <DeliveryBadge tone="blue">{BRAND_PROFILE.editionEn}</DeliveryBadge>
                <DeliveryBadge tone="light">Secure ERP Access</DeliveryBadge>
              </div>
            </div>

            <div className="mt-8 rounded-[28px] border border-blue-100 bg-gradient-to-r from-slate-950 via-blue-900 to-sky-600 p-6 text-white shadow-lg shadow-blue-950/15">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-blue-100">Business Operations Ready</p>
              <p className="mt-2 text-xl font-semibold">دخول آمن إلى منصة إدارة الأعمال</p>
              <p className="mt-2 text-sm leading-7 text-blue-50/90">نظام موحد لمتابعة العمليات اليومية، التحكم في الصلاحيات، إدارة البيانات، وإصدار التقارير بثقة.</p>
            </div>

            <div className="mt-6 flex mb-6 rounded-2xl bg-slate-100 p-1.5">
              <button
                type="button"
                onClick={() => setLoginType('company')}
                className={`flex-1 rounded-2xl px-4 py-3 text-sm font-semibold transition-all ${
                  loginType === 'company'
                    ? 'bg-white text-blue-700 shadow-sm ring-1 ring-blue-100'
                    : 'text-slate-500'
                }`}
              >
                دخول الشركة
              </button>
              <button
                type="button"
                onClick={() => setLoginType('super_admin')}
                className={`flex-1 rounded-2xl px-4 py-3 text-sm font-semibold transition-all ${
                  loginType === 'super_admin'
                    ? 'bg-white text-blue-700 shadow-sm ring-1 ring-blue-100'
                    : 'text-slate-500'
                }`}
              >
                دخول المشرف العام
              </button>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
                  ⚠️ {error}
                </div>
              )}

              {loginType === 'company' && (
                <div>
                  <label htmlFor="company_code" className="mb-2 block text-sm font-medium text-slate-700">
                    رمز الشركة *
                  </label>
                  <input
                    type="text"
                    id="company_code"
                    name="company_code"
                    required={loginType === 'company'}
                    value={formData.company_code}
                    onChange={handleInputChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    placeholder="أدخل رمز الشركة"
                  />
                </div>
              )}

              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700">
                  البريد الإلكتروني
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  placeholder="أدخل بريدك الإلكتروني"
                />
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-700">
                  كلمة المرور *
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  placeholder="أدخل كلمة المرور"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-slate-950 via-blue-900 to-sky-600 px-4 py-3.5 text-sm font-semibold text-white shadow-xl shadow-blue-950/15 transition hover:translate-y-[-1px] hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                    جاري تسجيل الدخول...
                  </>
                ) : (
                  'تسجيل الدخول'
                )}
              </button>
            </form>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">واجهة موحدة</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">تجربة استخدام تظهر بهوية الشركة داخل النظام</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">استمرارية العمل</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">نسخ احتياطي، تشخيص، وتفعيل من داخل النظام</p>
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white px-4 py-4 text-center" data-testid="copyright-notice">
              <p className="text-xs font-black text-slate-800 leading-relaxed">
                © 2026 منصة إدارة الأعمال — جميع الحقوق محفوظة
              </p>
              <p className="mt-2 text-[11px] font-bold text-slate-600 leading-relaxed">
                تم تصميم وتطوير هذا النظام بواسطة
                <span className="mx-1 text-sky-700">Hatem Mohamed Abdalla</span>
                <span className="block mt-1 text-slate-500" dir="ltr">📞 +971 50 390 9781</span>
              </p>
              <p className="mt-2 text-[10px] text-slate-500 leading-relaxed">
                حقوق الملكية الفكرية والنشر والاستخدام محفوظة حصرياً للمطور. يُمنع النسخ أو التوزيع أو التعديل أو إعادة الاستخدام بأي شكل بدون إذن كتابي مسبق. أي انتهاك يعرّض صاحبه للمساءلة القانونية.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};


const FirstRunLaunchGuide = ({ language, onNavigate, onDismiss, currentView, companySettings, user }) => {
  const isArabic = language === 'ar';
  const title = isArabic ? 'ابدأ استخدام النظام' : 'Getting started';
  const subtitle = isArabic
    ? 'هذه اللوحة تساعدك على إكمال الإعدادات الأساسية والوصول السريع إلى أهم مناطق النظام.'
    : 'This panel helps you complete basic setup and quickly reach the most important areas of the system.';

  return (
    <section className="mx-2 mb-4 rounded-[28px] border border-sky-100 bg-white/95 p-5 shadow-xl shadow-slate-900/5 md:mx-6 md:mt-5 md:p-6">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <DeliveryBadge tone="blue">{isArabic ? 'خطوات البداية' : 'Getting started'}</DeliveryBadge>
            <DeliveryBadge tone="light">{getCompanyDisplayName(companySettings, user, language)}</DeliveryBadge>
          </div>
          <h3 className="mt-3 text-2xl font-black tracking-tight text-slate-950">{title}</h3>
          <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-600">{subtitle}</p>
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {FIRST_RUN_ACTIONS.map((item, index) => {
              const isActive = resolveViewRoute(currentView) === resolveViewRoute(item.target || item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onNavigate(item.target || item.id)}
                  className={`rounded-2xl border px-4 py-4 text-start transition ${isActive ? 'border-blue-300 bg-blue-50 shadow-sm' : 'border-slate-200 bg-slate-50 hover:border-blue-200 hover:bg-white'}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-950 text-xs font-bold text-white">{index + 1}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900">{isArabic ? item.titleAr : item.titleEn}</p>
                      <p className="mt-1 text-sm leading-6 text-slate-600">{isArabic ? item.noteAr : item.noteEn}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="w-full rounded-[24px] bg-slate-950 p-5 text-white shadow-xl shadow-slate-900/15 xl:max-w-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-sky-200">{isArabic ? 'ملخص الحساب' : 'Account snapshot'}</p>
          <div className="mt-4 space-y-3 text-sm leading-7 text-slate-200">
            <p><span className="font-semibold text-white">{isArabic ? 'المنشأة:' : 'Company:'}</span> {companySettings?.company_name_ar || companySettings?.company_name || user?.company_name || BRAND_PROFILE.productNameAr}</p>
            <p><span className="font-semibold text-white">{isArabic ? 'المستخدم:' : 'Operator:'}</span> {user?.full_name || user?.email || '-'}</p>
            <p>{isArabic ? 'يمكنك إخفاء هذه اللوحة وإعادة فتحها لاحقًا من أعلى الشاشة.' : 'You can dismiss this panel and reopen it later from the top bar.'}</p>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => onNavigate('company-settings')}
              className="rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
            >
              {isArabic ? 'إعداد الشركة' : 'Company setup'}
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-2xl border border-white/20 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              {isArabic ? 'إخفاء اللوحة' : 'Dismiss guide'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main ERP Interface
// Main ERP Interface
const ERPInterface = ({ 
  user, 
  currentView, 
  setCurrentView, 
  sidebarCollapsed, 
  setSidebarCollapsed,
  showModal,
  setShowModal,
  logout,
  companySettings
}) => {
  // Language state with localStorage persistence
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('erp_language');
    return saved || 'ar';
  });

  // Mobile sidebar state - check initial value
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  // Phase 1c — Active branch selector (Topbar).  Hidden when the current
  // company has Branch Management OFF, so behaviour is bit-identical to
  // pre-branch releases for every company that hasn't opted in.
  // The Topbar hook only ever issues GET requests (feature + branches list).
  const branchApiFetch = React.useCallback((path) => {
    return apiGet(path.replace(/^\/api/, ''), { authContext: 'erp' });
  }, []);
  const activeBranch = useActiveBranch({
    companyCode: user?.company_code,
    apiFetch: branchApiFetch,
  });
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });

  const onboardingStorageKey = `sav_erp_first_run_guide_${user?.company_id || user?.company_code || user?.email || 'default'}_${CLIENT_BUILD_LABEL}`;
  const [showFirstRunGuide, setShowFirstRunGuide] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      return window.localStorage.getItem(onboardingStorageKey) !== 'dismissed';
    } catch (_err) {
      return true;
    }
  });

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Update document direction when language changes
  useEffect(() => {
    localStorage.setItem('erp_language', language);
    // Don't set document direction globally - it breaks mobile layout
    // document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'ar' ? 'en' : 'ar');
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      setShowFirstRunGuide(window.localStorage.getItem(onboardingStorageKey) !== 'dismissed');
    } catch (_err) {
      setShowFirstRunGuide(true);
    }
  }, [onboardingStorageKey]);

  const dismissFirstRunGuide = () => {
    setShowFirstRunGuide(false);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(onboardingStorageKey, 'dismissed');
    } catch (_err) {
      // ignore storage errors
    }
  };

  const reopenFirstRunGuide = () => {
    setShowFirstRunGuide(true);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(onboardingStorageKey);
    } catch (_err) {
      // ignore storage errors
    }
  };

  const jumpToFirstRunAction = (viewId) => {
    setCurrentView(resolveViewRoute(viewId));
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const t = (ar, en) => language === 'ar' ? ar : en;

  const isSuperAdminUser = String(user?.role || "").toLowerCase() === "super_admin";
  const activeModules = Array.isArray(user?.active_modules) ? user.active_modules : [];
  const moduleSelectionKnown = Array.isArray(user?.active_modules);
  const canUseModule = (moduleKey) => !moduleKey || !moduleSelectionKnown || activeModules.includes(moduleKey);
  const filterMenuItem = (item) => {
    if (!canUseModule(item.module)) return null;
    if (!Array.isArray(item.subItems)) return item;
    const subItems = item.subItems.map(filterMenuItem).filter(Boolean);
    if (!item.component && subItems.length === 0) return null;
    return { ...item, subItems };
  };

  const rawMenuItems = [
    { 
      id: 'dashboard', 
      name: t('لوحة التحكم', 'Dashboard'), 
      icon: '📊', 
      component: EnhancedDashboard 
    },
    {
      id: 'sav-one',
      name: t('SAV One - المركز الذكي', 'SAV One - Smart Center'),
      icon: '🧠',
      component: SavOneCommandCenter
    },
    {
      id: 'compliance-suite',
      name: t('الضرائب والجاهزية', 'Tax & Readiness'),
      icon: '🧾',
      subItems: [
        { id: 'tax-compliance-center', name: t('UAE Tax + e-invoicing', 'UAE Tax + e-invoicing'), component: TaxComplianceCenter },
        { id: 'einvoice-control-center', name: t('مركز الفوترة الإلكترونية', 'eInvoice Control Center'), component: EInvoiceControlCenter },
        { id: 'sav-ai-advisor', name: t('SAV Advisor و OCR', 'SAV Advisor & OCR'), component: SavAIAdvisor },
        { id: 'smart-bank-reconciliation', name: t('المطابقة البنكية الذكية', 'Smart Bank Reconciliation'), component: SmartBankReconciliationCenter },
        { id: 'smart-inventory-center', name: t('مستشار المخزون الذكي', 'Smart Inventory Advisor'), component: SmartInventoryCenter }
      ]
    },
    { 
      id: 'accounts', 
      name: t('الحسابات', 'Accounts'), 
      icon: '📋',
      subItems: [
        { id: 'chart-of-accounts', name: t('دليل الحسابات', 'Chart of Accounts'), component: ChartOfAccountsCenter },
        { id: 'journal-entries', name: t('قيود اليومية', 'Journal Entries'), component: JournalEntries },
        { id: 'recurring-journals', name: t('القيود المتكررة', 'Recurring Journals'), component: RecurringJournalsManager },
        { id: 'pdc', name: t('الشيكات المؤجلة', 'Post-Dated Checks'), component: PDCManager }
      ]
    },
    { 
      id: 'parties', 
      name: t('العملاء والموردين', 'Customers & Suppliers'), 
      icon: '🤝',
      subItems: [
        { id: 'customers', name: t('العملاء', 'Customers'), component: CustomersManager },
        { id: 'suppliers', name: t('الموردين', 'Suppliers'), component: SuppliersManager }
      ]
    },
    { 
      id: 'sales', 
      name: t('المبيعات', 'Sales'), 
      icon: '💰',
      subItems: [
        { id: 'enhanced-sales', name: t('إدارة المبيعات المحسنة', 'Enhanced Sales Manager'), component: EnhancedSalesManager },
        { id: 'sales-quotes', name: t('عروض أسعار المبيعات', 'Sales Quotes'), component: SalesQuotesManager },
        { id: 'sales-orders', name: t('أوامر المبيعات', 'Sales Orders'), component: SalesOrdersManager },
        { id: 'sales-invoices', name: t('فواتير المبيعات', 'Sales Invoices'), component: CustomerInvoiceCenter },
        { id: 'recurring-invoices', name: t('الفواتير الدورية', 'Recurring Invoices'), component: RecurringInvoicesManager },
        { id: 'credit-notes', name: t('إشعارات دائنة', 'Credit Notes'), component: CreditNotesManager },
        { id: 'delivery-notes', name: t('أذون التشغيل', 'Delivery Notes'), component: DeliveryNotesManager }
      ]
    },
    { 
      id: 'purchases', 
      name: t('المشتريات', 'Purchases'), 
      icon: '🛒',
      subItems: [
        { id: 'purchase-quotes', name: t('طلبات أسعار الشراء', 'Purchase Quotes'), component: PurchaseQuotesManager },
        { id: 'purchase-orders', name: t('أوامر الشراء', 'Purchase Orders'), component: PurchaseOrdersManager },
        { id: 'purchase-invoices', name: t('فواتير المشتريات', 'Purchase Invoices'), component: PurchaseInvoicesManager },
        { id: 'expense-invoices', name: t('فواتير المصروفات', 'Expense Invoices'), component: ExpenseInvoicesManager },
        { id: 'smart-invoice-scanner', name: t('مسح الفواتير الذكي', 'Smart Invoice Scanner'), component: SmartInvoiceScannerManager },
        { id: 'debit-notes', name: t('إشعارات مدينة', 'Debit Notes'), component: DebitNotesManager },
        { id: 'goods-receipts', name: t('إيصالات البضائع', 'Goods Receipts'), component: GoodsReceiptsManager }
      ]
    },
    { 
      id: 'banking', 
      name: t('البنوك والخزائن', 'Banking & Treasury'), 
      icon: '🏦',
      subItems: [
        { id: 'bank-accounts', name: t('الحسابات المصرفية', 'Bank Accounts'), component: BankAccountsManager },
        { id: 'receipts', name: t('سندات القبض', 'Receipts'), component: ReceiptsManager },
        { id: 'payments', name: t('سندات الصرف', 'Payments'), component: PaymentsManager },
        { id: 'auto-settlement', name: t('السداد التلقائي للفواتير', 'Automatic Invoice Settlement'), component: AutoSettlementCenter },
        { id: 'payroll-vouchers', name: t('سندات الرواتب', 'Payroll Vouchers'), component: PayrollVouchers },
        { id: 'transfers', name: t('التحويلات بين الحسابات', 'Inter-Account Transfers'), component: InterAccountTransfersManager },
        { id: 'reconciliation', name: t('تسوية البنك', 'Bank Reconciliation'), component: BankReconciliationsManager }
      ]
    },
    { 
      id: 'inventory', 
      name: t('المخزون', 'Inventory'), 
      icon: '📦', 
      component: InventoryManager 
    },
    { 
      id: 'hr', 
      name: t('الموارد البشرية', 'Human Resources'), 
      icon: '👥',
      subItems: [
        { id: 'employees', name: t('الموظفين', 'Employees'), component: HRManager },
        { id: 'expense-claims', name: t('مطالبات المصروفات', 'Expense Claims'), component: ExpenseClaimsManager },
        { id: 'projects', name: t('إدارة المشاريع', 'Project Management'), component: ProjectsManager }
      ]
    },
    { 
      id: 'assets', 
      name: t('الأصول الثابتة', 'Fixed Assets'), 
      icon: '🏗️', 
      component: AssetsManager 
    },
    { 
      id: 'manufacturing', 
      name: t('التصنيع', 'Manufacturing'), 
      icon: '🏭',
      module: 'manufacturing',
      subItems: [
        { id: 'manufacturing-manager', name: t('إدارة التصنيع', 'Manufacturing Manager'), component: ManufacturingManager },
        { id: 'bom', name: t('قائمة المواد', 'Bill of Materials'), component: BOMManager },
        { id: 'work-orders', name: t('أوامر العمل', 'Work Orders'), component: WorkOrderManager }
      ]
    },
    { 
      id: 'auto-repair',
      name: t('ورشة السيارات', 'Auto Repair Workshop'),
      icon: '🔧',
      module: 'auto_repair',
      component: AutoRepairManager
    },
    { 
      id: 'investments', 
      name: t('الاستثمارات', 'Investments'), 
      icon: '📈', 
      component: InvestmentsManager 
    },
    { 
      id: 'reports', 
      name: t('التقارير', 'Reports'), 
      icon: '📊', 
      subItems: [
        { id: 'reports-center', name: t('التقارير', 'Reports'), component: ReportsManager },
        { id: 'branch-trial-balance', name: t('ميزان مراجعة الفروع + المجمّع', 'Branch TB & Consolidated TB'), component: BranchTrialBalanceReports },
        { id: 'inter-branch-transfers', name: t('التحويلات بين الفروع', 'Inter-Branch Transfers'), component: InterBranchTransferManager },
        { id: 'global-document-search', name: t('محرك بحث المستندات والحركات', 'Document & Transaction Search'), component: GlobalDocumentSearch },
        { id: 'batch-print-center', name: t('طباعة المستندات بالفترة', 'Date-range Document Printing'), component: BatchPrintCenter }
      ]
    },
    { 
      id: 'settings', 
      name: t('الإعدادات', 'Settings'), 
      icon: '⚙️',
      subItems: [
        { id: 'company-settings', name: t('إعدادات الشركة', 'Company Settings'), component: CompanySettings },
        { id: 'gold-karat-settings', name: t('💎 إعدادات الذهب والعيارات', '💎 Gold & Karat Settings'), component: GoldKaratSettings },
        { id: 'user-management', name: t('إدارة المستخدمين', 'User Management'), component: UserManagement },
        { id: 'system-settings', name: t('إعدادات النظام', 'System Settings'), component: SystemSettingsManager },
        { id: 'subscription-status', name: t('حالة الاشتراك', 'Subscription Status'), component: CompanySubscriptionStatus },
        { id: 'diagnostics', name: t('مركز الفحص والإصلاح الشامل', 'Comprehensive Diagnostics & Repair Center'), component: DiagnosticsCenter },
        { id: 'financial-integrity', name: t('فحص النزاهة المالية', 'Financial Integrity'), component: FinancialIntegrityCenter },
        ...(isSuperAdminUser ? [
          { id: 'full-system-auditor', name: t('المراجع العدائي الشامل', 'Full-System Adversarial Auditor'), component: FullSystemAuditor },
          { id: 'manager-migration', name: t('استيراد / ترحيل من Manager', 'Import / Migrate from Manager'), component: ManagerMigration },
          { id: 'local-auto-repair', name: t('وكيل الإصلاح الذاتي المحلي', 'Local Auto-Repair Agent'), component: LocalAutoRepairAgent },
          { id: 'code-runtime-inspector', name: t('فاحص الكود والتشغيل', 'Code & Runtime Inspector'), component: CodeRuntimeInspector },
        ] : []),
        { id: 'period-locking', name: t('قفل الفترات المحاسبية', 'Period Locking'), component: PeriodLockingManager },
        { id: 'audit-logs', name: t('سجل التدقيق', 'Audit Logs'), component: AuditLogsManager },
        { id: 'backup', name: t('النسخ الاحتياطي', 'Backup'), component: BackupManager }
      ]
    },
    { 
      id: 'advanced', 
      name: t('الميزات المتقدمة', 'Advanced Features'), 
      icon: '🚀',
      subItems: [
        { id: 'smart-dashboard', name: t('الرؤى الذكية', 'Smart Insights'), component: SmartDashboard },
        { id: 'advanced-analytics', name: t('التحليلات المتقدمة', 'Advanced Analytics'), component: AdvancedAnalytics },
        { id: 'task-management', name: t('إدارة المهام', 'Task Management'), component: TaskManagement },
        { id: 'advanced-dashboard', name: t('ملخص العمليات', 'Operations Snapshot'), component: AdvancedDashboard },
        { id: 'advanced-settings', name: t('التهيئة المتقدمة', 'Advanced Configuration'), component: AdvancedSettings }
      ]
    }
  ];

  const menuItems = rawMenuItems.map(filterMenuItem).filter(Boolean);

  useEffect(() => {
    const parentWithCurrentView = menuItems.find((item) => Array.isArray(item.subItems) && item.subItems.some((subItem) => resolveViewRoute(subItem.id) === resolveViewRoute(currentView)));
    if (parentWithCurrentView) {
      setOpenMenuId(parentWithCurrentView.id);
    }
  }, [currentView]);

  const renderContent = () => {
    // Find the active component
    let ActiveComponent = null;
    const resolvedCurrentView = resolveViewRoute(currentView);
    
    for (const item of menuItems) {
      if (resolveViewRoute(item.id) === resolvedCurrentView && item.component) {
        ActiveComponent = item.component;
        break;
      }
      
      if (item.subItems) {
        const subItem = item.subItems.find(sub => resolveViewRoute(sub.id) === resolvedCurrentView);
        if (subItem?.component) {
          ActiveComponent = subItem.component;
          break;
        }
      }
    }

    if (ActiveComponent) {
      return <ActiveComponent 
        user={user} 
        currentUser={user}
        language={language}
        setShowModal={setShowModal}
        setCurrentView={(viewId) => setCurrentView(resolveViewRoute(viewId))}
        setCurrentPage={(viewId) => setCurrentView(resolveViewRoute(viewId))}
        currentView={resolvedCurrentView}
        companySettings={companySettings}
      />;
    }

    return <SummaryDashboard user={user} language={language} setShowModal={setShowModal} setCurrentPage={(viewId) => setCurrentView(resolveViewRoute(viewId))} />;
  };

  // Handle menu item click on mobile - close sidebar after selection
  const handleMobileMenuClick = (viewId) => {
    setCurrentView(resolveViewRoute(viewId));
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="h-screen w-full flex flex-row" style={{ direction: 'ltr' }}>
      {/* Mobile Menu Overlay */}
      {isMobile && mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Hidden on mobile, shown when menu is open */}
      <aside 
        className={`
          bg-white shadow-lg transition-all duration-300 flex h-full
          ${language === 'ar' ? 'border-l order-last' : 'border-r order-first'} border-gray-200
          ${isMobile 
            ? `fixed top-0 ${language === 'ar' ? 'right-0' : 'left-0'} z-50 transform ${mobileMenuOpen ? 'translate-x-0' : (language === 'ar' ? 'translate-x-full' : '-translate-x-full')}`
            : ''
          }
        `}
        style={{
          width: isMobile ? '280px' : (sidebarCollapsed ? '64px' : '256px'), 
          minWidth: isMobile ? '280px' : (sidebarCollapsed ? '64px' : '256px'), 
          flexDirection: 'column'
        }}
        dir={language === 'ar' ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="border-b border-slate-200 p-4">
          <div className="flex items-start justify-between gap-3">
            {(!sidebarCollapsed || isMobile) && (
              <div className="flex min-w-0 items-start gap-3">
                <BrandMark compact className="shrink-0" logoSrc={getCompanyLogo(companySettings)} label={getCompanyDisplayName(companySettings, user, language)} />
                <div className="min-w-0">
                  <h1 className="truncate text-base font-black text-slate-950 md:text-lg">
                    {getCompanyDisplayName(companySettings, user, language)}
                  </h1>
                  <p className="mt-1 truncate text-xs text-slate-500">{getCompanyDisplaySubtitle(companySettings, user, language)}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <DeliveryBadge tone="blue">{language === 'ar' ? BRAND_PROFILE.editionAr : BRAND_PROFILE.editionEn}</DeliveryBadge>
                  </div>
                </div>
              </div>
            )}
            {!isMobile && (
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="rounded-xl p-2 transition-colors hover:bg-slate-100"
              >
                {sidebarCollapsed ? (language === 'ar' ? '←' : '→') : (language === 'ar' ? '→' : '←')}
              </button>
            )}
            {isMobile && (
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-xl p-2 transition-colors hover:bg-slate-100"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav style={{flex: '1 1 auto', width: '100%', overflowY: 'auto'}}>
          <div className="px-2 py-4 space-y-1">
            {menuItems.map(item => (
              <MenuItem 
                key={item.id}
                item={item}
                currentView={currentView}
                setCurrentView={isMobile ? handleMobileMenuClick : setCurrentView}
                collapsed={!isMobile && sidebarCollapsed}
                language={language}
                openMenuId={openMenuId}
                setOpenMenuId={setOpenMenuId}
              />
            ))}
          </div>
        </nav>

        {/* User Section */}
        <div className="mt-auto border-t border-slate-200 p-4">
          {(!sidebarCollapsed || isMobile) && (
            <div className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-sm font-semibold text-slate-900">{user?.full_name}</p>
              <p className="mt-1 truncate text-xs text-slate-500">{user?.email}</p>
              <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-sky-700">{getCompanyDisplayName(companySettings, user, language)}</p>
            </div>
          )}
          <button
            onClick={logout}
            className={`flex w-full items-center ${sidebarCollapsed && !isMobile ? 'justify-center' : ''} rounded-xl px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50`}
            title={t('تسجيل الخروج', 'Logout')}
          >
            <span className="text-lg">🚪</span>
            {(!sidebarCollapsed || isMobile) && <span className={language === 'ar' ? 'mr-2' : 'ml-2'}>{t('تسجيل الخروج', 'Logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col overflow-hidden w-full ${language === 'ar' ? 'text-right' : 'text-left'}`} style={{ minWidth: 0 }}>
        {/* Top Bar */}
        <header className="border-b border-slate-200 bg-white/80 px-4 py-3 shadow-sm backdrop-blur md:px-6 md:py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              {/* Mobile Menu Button */}
              {isMobile && (
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className="rounded-xl p-2 transition-colors hover:bg-slate-100"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate text-lg font-bold text-slate-950 md:text-xl">
                    {getPageTitle(currentView, menuItems)}
                  </h2>
                  <DeliveryBadge tone="blue">{language === 'ar' ? 'نظام الأعمال' : 'Business Suite'}</DeliveryBadge>
                </div>
                <p className="mt-1 truncate text-xs text-slate-500">{getCompanyDisplaySubtitle(companySettings, user, language)}</p>
              </div>
            </div>
            <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse' : ''} space-x-2 md:space-x-4`}>
              {isSuperAdminUser && (typeof window !== 'undefined') && /preview|emergentagent|localhost/i.test(window.location.hostname) && (
                <a
                  href="/site"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="sav-software-preview-shortcut"
                  className="hidden items-center gap-1 rounded-xl bg-[#1C3A63] px-2 py-2 text-xs font-bold text-white transition-colors hover:bg-[#0E1F39] md:inline-flex md:gap-2 md:px-3 md:text-sm"
                  title={t('معاينة موقع SAV Software (سوبر أدمن فقط)', 'SAV Software Preview (Super Admin only)')}
                >
                  🌐 {t('معاينة SAV Software', 'SAV Software Preview')}
                </a>
              )}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1 rounded-xl bg-blue-100 px-2 py-2 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-200 md:gap-2 md:px-3 md:text-sm"
                title={t('تبديل اللغة', 'Toggle Language')}
              >
                🌐 {language === 'ar' ? 'EN' : 'عربي'}
              </button>
                            <button
                onClick={reopenFirstRunGuide}
                className="flex items-center gap-1 rounded-xl bg-white px-2 py-2 text-xs font-medium text-slate-700 ring-1 ring-slate-200 transition-colors hover:bg-slate-50 md:gap-2 md:px-3 md:text-sm"
                title={t('إعادة فتح دليل التشغيل الأول', 'Reopen first-run guide')}
              >
                🚀 {t('دليل البدء', 'Launch guide')}
              </button>
              {user ? <NotificationBell language={language} currentUser={user} /> : null}
              <BranchSelector
                mode={activeBranch.mode}
                branches={activeBranch.branches}
                activeBranchId={activeBranch.activeBranchId}
                onChange={activeBranch.setActiveBranch}
              />

            </div>
          </div>
          <div className="sav-brand-divider mt-3 h-1 w-full rounded-full" />
        </header>

        {/* Page Content */}
        <LanguageContext.Provider value={{ language, t, toggleLanguage }}>
          <main className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50 p-2 md:p-0 w-full" style={{ minWidth: 0 }}>
            {showFirstRunGuide && (
              <FirstRunLaunchGuide
                language={language}
                onNavigate={jumpToFirstRunAction}
                onDismiss={dismissFirstRunGuide}
                currentView={currentView}
                companySettings={companySettings}
                user={user}
              />
            )}
            {renderContent && (
              <Suspense fallback={<LazyFallback />}>
                {renderContent()}
              </Suspense>
            )}
          </main>
        </LanguageContext.Provider>
      </div>

      {/* Modals */}
      {showModal === 'customer' && (
        <CustomerModal 
          onClose={() => setShowModal(null)} 
          user={user}
          language={language}
        />
      )}
      {showModal === 'supplier' && (
        <SupplierModal 
          onClose={() => setShowModal(null)} 
          user={user}
          language={language}
        />
      )}
      {showModal === 'account' && (
        <AccountModal 
          onClose={() => setShowModal(null)} 
          user={user}
          language={language}
        />
      )}
    </div>
  );
};

// Menu Item Component
const MenuItem = ({ item, currentView, setCurrentView, collapsed, language, openMenuId, setOpenMenuId }) => {
  const isActive = resolveViewRoute(currentView) === resolveViewRoute(item.target || item.id);
  const hasSubItems = item.subItems && item.subItems.length > 0;
  const isRTL = language === 'ar';
  const expanded = Boolean(hasSubItems && openMenuId === item.id);

  const handleClick = () => {
    if (hasSubItems) {
      setOpenMenuId(expanded ? null : item.id);
    } else {
      setOpenMenuId(null);
      setCurrentView(resolveViewRoute(item.id));
    }
  };

  return (
    <div>
      <button
        onClick={handleClick}
        className={`w-full flex items-center ${collapsed ? 'justify-center' : 'justify-between'} px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
          isActive ? 'bg-gradient-to-r from-blue-50 to-sky-50 text-blue-700 ring-1 ring-blue-100 shadow-sm' : 'text-gray-700 hover:bg-slate-50'
        }`}
        title={collapsed ? item.name : ''}
      >
        <div className="flex items-center">
          <span className="text-lg">{item.icon}</span>
          {!collapsed && <span className={isRTL ? 'mr-3' : 'ml-3'}>{item.name}</span>}
        </div>
        {!collapsed && hasSubItems && (
          <span className={`transform transition-transform ${expanded ? (isRTL ? '-rotate-90' : 'rotate-90') : ''}`}>
            {isRTL ? '◀' : '▶'}
          </span>
        )}
      </button>

      {/* Sub Items */}
      {!collapsed && hasSubItems && expanded && (
        <div className={`mt-1 ${isRTL ? 'mr-6' : 'ml-6'} space-y-1`}>
          {item.subItems.map(subItem => (
            <button
              key={subItem.id}
              onClick={() => setCurrentView(resolveViewRoute(subItem.id))}
              className={`w-full ${isRTL ? 'text-right' : 'text-left'} px-3 py-2 text-sm rounded-lg transition-colors ${
                resolveViewRoute(currentView) === resolveViewRoute(subItem.id) ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-100' : 'text-gray-600 hover:bg-slate-50'
              }`}
            >
              {subItem.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper function to get page title
const getPageTitle = (currentView, menuItems) => {
  const resolvedCurrentView = resolveViewRoute(currentView);
  for (const item of menuItems) {
    if (resolveViewRoute(item.id) === resolvedCurrentView) {
      return item.name;
    }

    if (item.subItems) {
      const subItem = item.subItems.find(sub => resolveViewRoute(sub.id) === resolvedCurrentView);
      if (subItem) {
        return subItem.name;
      }
    }
  }

  return 'لوحة التحكم';
};

export default FullERPApp;